import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectInput,
  SelectBooleanInput,
  TextInput,
  TextInputModes
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import { CardBlock } from '../../card';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';

import {
  Mode,
  PHQMode,
  phqModeFromSchedule,
  scheduleFromPHQMode
} from '../../../../libs/apps/epxdepress';

const NUM_ITEMS = {
  D: 7,
  T: 3,
  O: 2,
  W: 1,
  C: null
};

interface EpxDepressData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  mode?: Mode;
  times: string[];
  days: number[];
  step?: number;
  suicide?: boolean;
  suicideContact?: boolean;
  supportName?: string;
  supportPhone?: string;
  hasPHQ?: boolean;
  phqFrequency?: number;
  isPHQScreening?: boolean;
  phqMode?: PHQMode;
  hasGranularPHQ9Alerts?: boolean;
  hasGAD?: boolean;
  gadFrequency?: number;
  hasCOVID?: boolean;
}

type EpxDepressDataRaw = DateSerialized<EpxDepressData, 'start' | 'end'>;

export const EpxDepressDefaultData: EpxDepressData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  mode: 'T',
  times: ['10:00'],
  days: [1, 3, 5],
  hasPHQ: true,
  phqFrequency: 4,
  isPHQScreening: false,
  phqMode: 'phq9',
  hasGranularPHQ9Alerts: false,
  hasGAD: false,
  gadFrequency: 4,
  hasCOVID: false,
  suicide: true,
  suicideContact: false
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxDepressFormProps {
  setForm?: (form: FormInstance<EpxDepressDataRaw>) => void;
  onSubmit?: (data: EpxDepressData) => void;
  isDisabled?: boolean;
}

const EpxDepressForm = ({
  setForm,
  onSubmit,
  isDisabled
}: EpxDepressFormProps): JSX.Element => {
  const serialize = (data: EpxDepressDataRaw): EpxDepressData => {
    const phqFlags = scheduleFromPHQMode((data.phqMode as PHQMode) || 'none');
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      hasPHQ: phqFlags.hasPHQ,
      isPHQScreening: phqFlags.isPHQScreening,
      phqFrequency:
        data.phqFrequency != null ? parseInt(data.phqFrequency as any) : null,
      gadFrequency:
        data.gadFrequency != null ? parseInt(data.gadFrequency as any) : null
    };
  };
  const deserialize = (data: EpxDepressData): EpxDepressDataRaw => {
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT),
      phqMode: data.phqMode ?? phqModeFromSchedule(data)
    };
  };
  const defaultData = deserialize(EpxDepressDefaultData);
  const form = new FormInstance<EpxDepressDataRaw, EpxDepressData>(
    defaultData,
    { serialize, deserialize }
  );
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        {(props) => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                  <Col sm={12}>
                    <SelectLanguage />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <StartDate label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={12}>
                    <SelectTime label="Message At" name="times[0]" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectInput
                      label="Mood Question Frequency"
                      name="mode"
                      options={[
                        {
                          label: '1 Time per Week',
                          value: 'W'
                        },
                        {
                          label: '2 Times per Week',
                          value: 'O'
                        },
                        {
                          label: '3 Times per Week',
                          value: 'T'
                        },
                        {
                          label: 'Custom Days',
                          value: 'C'
                        }
                      ]}
                      fill
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={24}>
                    <Weekday
                      name="days"
                      numItems={NUM_ITEMS[props.values.mode]}
                      minItems={1}
                      abbreviated
                      inline
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectInput
                      label="PHQ Module"
                      name="phqMode"
                      options={[
                        {
                          label: 'None',
                          value: 'none'
                        },
                        {
                          label: 'PHQ-9 Only',
                          value: 'phq9'
                        },
                        {
                          label: 'PHQ-2 \u2192 PHQ-9 (Screening)',
                          value: 'phq2_to_phq9'
                        }
                      ]}
                      fill
                    />
                  </Col>
                  {props.values.phqMode !== 'none' ? (
                    <Col sm={12}>
                      <SelectInput
                        label="PHQ Frequency"
                        name="phqFrequency"
                        options={[
                          {
                            label: 'Every 2 Weeks',
                            value: 2
                          },
                          {
                            label: 'Every 4 Weeks',
                            value: 4
                          },
                          {
                            label: 'Every 12 Weeks',
                            value: 12
                          }
                        ]}
                        fill
                      />
                    </Col>
                  ) : null}
                </Row>
                {props.values.phqMode !== 'none' ? (
                  <>
                    <Row>
                      <Col sm={12}>
                        <SelectBooleanInput
                          label="Ask Question about Suicide"
                          name="suicide"
                          trueLabel="Yes"
                          falseLabel="No"
                          fill
                        />
                      </Col>
                      <Col sm={12}>
                        <SelectBooleanInput
                          label="Use National Suicide Hotline"
                          name="suicideContact"
                          trueLabel="No"
                          falseLabel="Yes"
                          fill
                        />
                      </Col>
                    </Row>
                    {props.values.suicideContact ? (
                      <Row>
                        <Col sm={12}>
                          <TextInput
                            label="Support Line Name"
                            name="supportName"
                            fill
                          />
                        </Col>
                        <Col sm={12}>
                          <TextInput
                            label="Support Line Phone Number"
                            name="supportPhone"
                            mode={TextInputModes.PhoneStrict}
                            fill
                          />
                        </Col>
                      </Row>
                    ) : null}
                    <Row>
                      <Col sm={12}>
                        <SelectBooleanInput
                          label="Use Granular PHQ-9 Alerts"
                          name="hasGranularPHQ9Alerts"
                          fill
                        />
                      </Col>
                    </Row>
                  </>
                ) : null}
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput label="Ask GAD-7" name="hasGAD" fill />
                  </Col>
                  <Col sm={12}>
                    <SelectInput
                      label="GAD-7 Frequency"
                      name="gadFrequency"
                      options={[
                        {
                          label: 'Every 2 Weeks',
                          value: 2
                        },
                        {
                          label: 'Every 4 Weeks',
                          value: 4
                        },
                        {
                          label: 'Every 12 Weeks',
                          value: 12
                        }
                      ]}
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Use COVID BH Module"
                      name="hasCOVID"
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxDepressForm;
